"use client";

import { CheckCircleIcon } from "@heroicons/react/20/solid";
import confetti from "canvas-confetti";
import * as React from "react";
import { cn } from "../../../lib";
import {
	CONTENT_TOOLTIP_SHORT,
	ContentTooltip,
} from "../../design-system/tooltip";
import { PulseRing } from "../../pulse-ring";
import { AnimatedCircularProgressBar } from "../animated-circular-progress-bar";

const LANDING_IMG = "/images/landing-page";

/** All path stop overlays (including boss) share this size — larger than env/topic defaults. */
const PATH_STOP_OVERLAY_ICON_SIZE_CLASS = "h-8 w-8";

export type HexagonImageSrc = string | { src?: string };

type StopIcon = {
	icon: string | HexagonImageSrc;
	type: string;
	title: string;
	description: string;
};

/** Tooltip metadata for preset landing-page path stops (matches demo `PATH_ICONS` order). */
type LandingPathStopIcon = StopIcon;

const STOP_ICONS: StopIcon[] = [
	{
		icon: `${LANDING_IMG}/stop-milestone.png`,
		type: "milestone",
		title: "Milestone",
		description: "Self-assessed checkpoint",
	},
	{
		icon: `${LANDING_IMG}/stop-learn.png`,
		type: "learn",
		title: "Learn",
		description: "Micro-learning topics",
	},
	{
		icon: `${LANDING_IMG}/stop-teach.png`,
		type: "teach",
		title: "Teach",
		description: "Share your knowledge",
	},
	{
		icon: `${LANDING_IMG}/stop-chat.png`,
		type: "chat",
		title: "Chat",
		description: "Role-play scenarios",
	},
	{
		icon: `${LANDING_IMG}/stop-complete.png`,
		type: "complete",
		title: "Revise",
		description: "Practice sticking points",
	},
	{
		icon: `${LANDING_IMG}/stop-treasure.png`,
		type: "treasure",
		title: "Treasure",
		description: "Discover gold & rewards",
	},
	{
		icon: `${LANDING_IMG}/stop-battle.png`,
		type: "battle",
		title: "Battle",
		description: "Real-time quizzes",
	},
	{
		icon: `${LANDING_IMG}/stop-assignment.png`,
		type: "assignment",
		title: "Assignment",
		description: "Real-world tasks",
	},
];

/** First six presets: milestone → treasure (used by marketing solution demo path). */
const LANDING_PAGE_DEMO_PATH_STOPS: readonly LandingPathStopIcon[] =
	STOP_ICONS.slice(0, 6);

// Helper function to get all neighbors of a hexagon in a hexagonal grid
function getNeighbors(
	row: number,
	col: number,
): Array<{ row: number; col: number }> {
	const isOddRow = (row + 1) % 2 === 1;
	if (isOddRow) {
		return [
			{ row: row - 1, col },
			{ row: row - 1, col: col - 1 },
			{ row, col: col - 1 },
			{ row, col: col + 1 },
			{ row: row + 1, col },
			{ row: row + 1, col: col - 1 },
		];
	}
	return [
		{ row: row - 1, col },
		{ row: row - 1, col: col + 1 },
		{ row, col: col - 1 },
		{ row, col: col + 1 },
		{ row: row + 1, col },
		{ row: row + 1, col: col + 1 },
	];
}

// Check if a move from current position to neighbor is a backward (left) move
function isBackwardMove(
	currentRow: number,
	currentCol: number,
	neighborRow: number,
	neighborCol: number,
): boolean {
	const isOddRow = (currentRow + 1) % 2 === 1;
	if (isOddRow) {
		// For odd rows, backward moves are: (row-1, col-1), (row, col-1), (row+1, col-1)
		return (
			(neighborRow === currentRow - 1 &&
				neighborCol === currentCol - 1) ||
			(neighborRow === currentRow && neighborCol === currentCol - 1) ||
			(neighborRow === currentRow + 1 && neighborCol === currentCol - 1)
		);
	}
	// For even rows, backward moves are: (row-1, col-1), (row, col-1), (row+1, col-1)
	return (
		(neighborRow === currentRow - 1 && neighborCol === currentCol - 1) ||
		(neighborRow === currentRow && neighborCol === currentCol - 1) ||
		(neighborRow === currentRow + 1 && neighborCol === currentCol - 1)
	);
}

// Check if a move from current position to neighbor is a forward (right) move
function isForwardMove(
	currentRow: number,
	currentCol: number,
	neighborRow: number,
	neighborCol: number,
): boolean {
	const isOddRow = (currentRow + 1) % 2 === 1;
	if (isOddRow) {
		// For odd rows, forward moves are: (row-1, col), (row, col+1), (row+1, col)
		return (
			(neighborRow === currentRow - 1 && neighborCol === currentCol) ||
			(neighborRow === currentRow && neighborCol === currentCol + 1) ||
			(neighborRow === currentRow + 1 && neighborCol === currentCol)
		);
	}
	// For even rows, forward moves are: (row-1, col+1), (row, col+1), (row+1, col+1)
	return (
		(neighborRow === currentRow - 1 && neighborCol === currentCol + 1) ||
		(neighborRow === currentRow && neighborCol === currentCol + 1) ||
		(neighborRow === currentRow + 1 && neighborCol === currentCol + 1)
	);
}

// Determine the direction from current position to next position in a hexagonal grid
function getDirection(
	currentRow: number,
	currentCol: number,
	nextRow: number,
	nextCol: number,
): "upRight" | "right" | "downRight" | "upLeft" | "left" | "downLeft" {
	const rowDiff = nextRow - currentRow;
	const colDiff = nextCol - currentCol;
	const isOddRow = (currentRow + 1) % 2 === 1;

	// Right direction (always col+1, row same)
	if (rowDiff === 0 && colDiff === 1) {
		return "right";
	}

	// Left direction (always col-1, row same)
	if (rowDiff === 0 && colDiff === -1) {
		return "left";
	}

	if (!isOddRow) {
		// Even row neighbors: (row-1, col), (row-1, col-1), (row, col-1), (row, col+1), (row+1, col), (row+1, col-1)
		if (rowDiff === -1 && colDiff === 0) {
			return "upLeft"; // (row-1, col) - up-left
		}
		if (rowDiff === -1 && colDiff === -1) {
			return "upLeft"; // (row-1, col-1) - up-left
		}
		if (rowDiff === 1 && colDiff === 0) {
			return "downLeft"; // (row+1, col) - down-left
		}
		if (rowDiff === 1 && colDiff === -1) {
			return "downLeft"; // (row+1, col-1) - down-left
		}
	} else {
		// Odd row neighbors: (row-1, col), (row-1, col+1), (row, col-1), (row, col+1), (row+1, col), (row+1, col+1)
		if (rowDiff === -1 && colDiff === 0) {
			return "upRight"; // (row-1, col) - up-right
		}
		if (rowDiff === -1 && colDiff === 1) {
			return "upRight"; // (row-1, col+1) - up-right
		}
		if (rowDiff === 1 && colDiff === 0) {
			return "downRight"; // (row+1, col) - down-right
		}
		if (rowDiff === 1 && colDiff === 1) {
			return "downRight"; // (row+1, col+1) - down-right
		}
	}

	// Fallback based on general direction (shouldn't normally reach here)
	if (rowDiff < 0 && colDiff > 0) {
		return "upRight";
	}
	if (rowDiff < 0 && colDiff <= 0) {
		return "upLeft";
	}
	if (rowDiff > 0 && colDiff > 0) {
		return "downRight";
	}
	if (rowDiff > 0 && colDiff <= 0) {
		return "downLeft";
	}

	// Default fallback
	return "right";
}

// Check if a path is valid (each hexagon has exactly 2 adjacent hexagons except start/end)
function isValidPath(path: Array<{ row: number; col: number }>): boolean {
	const pathSet = new Set(path.map((p) => `${p.row},${p.col}`));

	for (let i = 0; i < path.length; i++) {
		const current = path[i];
		const neighbors = getNeighbors(current.row, current.col);
		const adjacentInPath = neighbors.filter((n) =>
			pathSet.has(`${n.row},${n.col}`),
		);

		const expectedAdjacent = i === 0 || i === path.length - 1 ? 1 : 2;
		if (adjacentInPath.length !== expectedAdjacent) {
			return false;
		}
	}

	return true;
}

// Generate a random path from start to end
function generatePath(
	config: Required<Omit<PathConfig, "refreshKey">>,
): Array<{ row: number; col: number }> {
	const {
		startRow,
		startCol,
		endRow,
		endCol,
		yMinRange,
		yMaxRange,
		xMaxStepsBack,
		xMaxStepsForward,
	} = config;

	const maxAttempts = 50;
	let attempts = 0;

	while (attempts < maxAttempts) {
		const path: Array<{ row: number; col: number }> = [
			{ row: startRow, col: startCol },
		];
		const pathSet = new Set<string>([`${startRow},${startCol}`]);
		let currentRow = startRow;
		let currentCol = startCol;
		let consecutiveBackward = 0;
		let consecutiveForward = 0;
		const maxPathLength = Math.abs(endCol - startCol) * 3;
		let stepCount = 0;

		while (stepCount < maxPathLength) {
			stepCount++;

			// 0) Check if current path is valid - if not, move to next attempt
			// Skip validation check if path only has 1 element (just the start)
			if (path.length > 1 && !isValidPath(path)) {
				break; // Move to next attempt
			}

			// 1) Get neighbors of current hexagon
			const neighbors = getNeighbors(currentRow, currentCol);

			// 2) If end hexagon is one of the neighbors, step to it immediately and return
			const endNeighbor = neighbors.find(
				(n) => n.row === endRow && n.col === endCol,
			);
			if (endNeighbor && !pathSet.has(`${endRow},${endCol}`)) {
				const rowOffset = endRow - startRow;
				if (rowOffset >= yMinRange && rowOffset <= yMaxRange) {
					path.push(endNeighbor);
					if (isValidPath(path)) {
						return path;
					}
				}
			}

			// 3) Filter out neighbors that are already in the path (should only be 1 - the previous one)
			let validMoves = neighbors.filter(
				(n) => !pathSet.has(`${n.row},${n.col}`),
			);

			// 4) Filter out neighbors that are neighbors to the previous hexagon (if not the first one)
			if (path.length > 1) {
				const prevHex = path[path.length - 2]; // Get the previous hexagon (not the current one)
				const prevNeighbors = getNeighbors(prevHex.row, prevHex.col);
				const prevNeighborsSet = new Set(
					prevNeighbors.map((n) => `${n.row},${n.col}`),
				);
				validMoves = validMoves.filter(
					(n) => !prevNeighborsSet.has(`${n.row},${n.col}`),
				);
			}

			// 5) Filter out ones outside yMinRange/yMaxRange, and backward moves to top/bottom row
			// Also filter out backward moves for the first two steps (index 0 or 1)
			// Filter out neighbors to the right of the end hexagon (cannot overshoot)
			const topRow = startRow + yMinRange;
			const bottomRow = startRow + yMaxRange;
			const isFirstTwoSteps = path.length <= 2; // Index 0 or 1

			validMoves = validMoves.filter((n) => {
				const rowOffset = n.row - startRow;
				// Must be within y range
				if (rowOffset < yMinRange || rowOffset > yMaxRange) {
					return false;
				}
				// Filter out neighbors to the right of the end hexagon (cannot overshoot)
				if (n.col > endCol) {
					return false;
				}
				// For first two steps, filter out backward moves (must move forward)
				if (
					isFirstTwoSteps &&
					isBackwardMove(currentRow, currentCol, n.row, n.col)
				) {
					return false;
				}
				// Filter out backward moves to top or bottom row
				if (
					isBackwardMove(currentRow, currentCol, n.row, n.col) &&
					(n.row === topRow || n.row === bottomRow)
				) {
					return false;
				}
				return true;
			});

			// 6) If no valid options, move to next attempt
			if (validMoves.length === 0) {
				break;
			}

			// 7) If only one valid option, take it
			if (validMoves.length === 1) {
				const chosenMove = validMoves[0];
				const isBackward = isBackwardMove(
					currentRow,
					currentCol,
					chosenMove.row,
					chosenMove.col,
				);
				const isForward = isForwardMove(
					currentRow,
					currentCol,
					chosenMove.row,
					chosenMove.col,
				);
				path.push(chosenMove);
				pathSet.add(`${chosenMove.row},${chosenMove.col}`);
				currentRow = chosenMove.row;
				currentCol = chosenMove.col;
				consecutiveBackward = isBackward ? consecutiveBackward + 1 : 0;
				consecutiveForward = isForward ? consecutiveForward + 1 : 0;
				continue;
			}

			// 7) Multiple valid options - filter by consecutive limits
			const movesBeforeConsecutiveFilter = [...validMoves];
			validMoves = validMoves.filter((n) => {
				const isBackward = isBackwardMove(
					currentRow,
					currentCol,
					n.row,
					n.col,
				);
				const isForward = isForwardMove(
					currentRow,
					currentCol,
					n.row,
					n.col,
				);
				if (isBackward && consecutiveBackward >= xMaxStepsBack) {
					return false;
				}
				if (isForward && consecutiveForward >= xMaxStepsForward) {
					return false;
				}
				return true;
			});

			// If no options left after consecutive filter, ignore limits and use moves before filter
			if (validMoves.length === 0) {
				validMoves = movesBeforeConsecutiveFilter;
			}

			// If only one option left, take it
			if (validMoves.length === 1) {
				const chosenMove = validMoves[0];
				const isBackward = isBackwardMove(
					currentRow,
					currentCol,
					chosenMove.row,
					chosenMove.col,
				);
				const isForward = isForwardMove(
					currentRow,
					currentCol,
					chosenMove.row,
					chosenMove.col,
				);
				path.push(chosenMove);
				pathSet.add(`${chosenMove.row},${chosenMove.col}`);
				currentRow = chosenMove.row;
				currentCol = chosenMove.col;
				consecutiveBackward = isBackward ? consecutiveBackward + 1 : 0;
				consecutiveForward = isForward ? consecutiveForward + 1 : 0;
				continue;
			}

			// Multiple options - choose randomly with 60% right, 40% left probability
			const forwardMoves = validMoves.filter((m) =>
				isForwardMove(currentRow, currentCol, m.row, m.col),
			);
			const backwardMoves = validMoves.filter((m) =>
				isBackwardMove(currentRow, currentCol, m.row, m.col),
			);
			const sameColMoves = validMoves.filter(
				(m) =>
					m.col === currentCol &&
					!isForwardMove(currentRow, currentCol, m.row, m.col) &&
					!isBackwardMove(currentRow, currentCol, m.row, m.col),
			);

			let chosenMove: { row: number; col: number };

			if (forwardMoves.length > 0 && backwardMoves.length > 0) {
				// Both directions available - 60% right, 40% left
				if (Math.random() < 0.6) {
					chosenMove =
						forwardMoves[
							Math.floor(Math.random() * forwardMoves.length)
						];
				} else {
					chosenMove =
						backwardMoves[
							Math.floor(Math.random() * backwardMoves.length)
						];
				}
			} else if (forwardMoves.length > 0) {
				chosenMove =
					forwardMoves[
						Math.floor(Math.random() * forwardMoves.length)
					];
			} else if (backwardMoves.length > 0) {
				chosenMove =
					backwardMoves[
						Math.floor(Math.random() * backwardMoves.length)
					];
			} else if (sameColMoves.length > 0) {
				chosenMove =
					sameColMoves[
						Math.floor(Math.random() * sameColMoves.length)
					];
			} else {
				break; // No valid moves (shouldn't happen)
			}

			const isBackward = isBackwardMove(
				currentRow,
				currentCol,
				chosenMove.row,
				chosenMove.col,
			);
			const isForward = isForwardMove(
				currentRow,
				currentCol,
				chosenMove.row,
				chosenMove.col,
			);
			path.push(chosenMove);
			pathSet.add(`${chosenMove.row},${chosenMove.col}`);
			currentRow = chosenMove.row;
			currentCol = chosenMove.col;
			consecutiveBackward = isBackward ? consecutiveBackward + 1 : 0;
			consecutiveForward = isForward ? consecutiveForward + 1 : 0;
		}

		// Check if we reached the end with a valid path
		const last = path[path.length - 1];
		if (last.row === endRow && last.col === endCol && isValidPath(path)) {
			return path;
		}

		attempts++;
	}

	// Fallback: return a simple path if generation fails
	const fallbackPath: Array<{ row: number; col: number }> = [];
	for (let c = startCol; c <= endCol; c++) {
		fallbackPath.push({ row: startRow, col: c });
	}
	return fallbackPath;
}

type PathConfig = {
	startRow?: number;
	startCol?: number;
	endRow?: number;
	endCol?: number;
	yMinRange?: number;
	yMaxRange?: number;
	xMaxStepsBack?: number;
	xMaxStepsForward?: number;
	refreshKey?: number;
};

// Calculate hexagonal distance between two hexagons using BFS
// Returns the minimum number of hexagon steps between two positions
function hexagonalDistance(
	row1: number,
	col1: number,
	row2: number,
	col2: number,
	isDirectPath = true,
): number {
	// If same position, distance is 0
	if (row1 === row2 && col1 === col2) {
		return 0;
	}

	// Use BFS (Breadth-First Search) to find shortest path
	const queue: Array<{ row: number; col: number; distance: number }> = [];
	const visited = new Set<string>();

	queue.push({ row: row1, col: col1, distance: 0 });
	visited.add(`${row1},${col1}`);

	// Helper function to calculate Manhattan distance (used as heuristic for direct path)
	const manhattanDistance = (
		r1: number,
		c1: number,
		r2: number,
		c2: number,
	) => {
		return Math.abs(r1 - r2) + Math.abs(c1 - c2);
	};

	while (queue.length > 0) {
		const current = queue.shift();
		if (!current) {
			break;
		}

		// If we reached the target, return the distance
		if (current.row === row2 && current.col === col2) {
			return current.distance;
		}

		// Get all neighbors
		let neighbors = getNeighbors(current.row, current.col);

		// If isDirectPath is true, filter neighbors to only those moving toward the target
		if (isDirectPath) {
			const currentManhattanDistance = manhattanDistance(
				current.row,
				current.col,
				row2,
				col2,
			);
			neighbors = neighbors.filter((neighbor) => {
				const neighborManhattanDistance = manhattanDistance(
					neighbor.row,
					neighbor.col,
					row2,
					col2,
				);
				// Only keep neighbors that are closer to or at the same distance from the target
				// (allowing same distance helps with cases where we need to move around obstacles)
				return neighborManhattanDistance <= currentManhattanDistance;
			});

			// If no neighbors are closer, fall back to all neighbors (shouldn't happen often)
			if (neighbors.length === 0) {
				neighbors = getNeighbors(current.row, current.col);
			}
		}

		// Check filtered neighbors (all neighbors if isDirectPath is false)
		for (const neighbor of neighbors) {
			const key = `${neighbor.row},${neighbor.col}`;
			if (!visited.has(key)) {
				visited.add(key);
				queue.push({
					row: neighbor.row,
					col: neighbor.col,
					distance: current.distance + 1,
				});
			}
		}
	}

	// Should not reach here, but return a large number if paths don't connect
	return Number.POSITIVE_INFINITY;
}

// Calculate distance from a hexagon to the path (actual hexagonal grid distance)
// Returns the minimum number of hexagon steps to the nearest path hexagon
function distanceToPath(
	row: number,
	col: number,
	path: Array<{ row: number; col: number }>,
): number {
	if (path.length === 0) {
		return Number.POSITIVE_INFINITY;
	}

	let minDistance = Number.POSITIVE_INFINITY;
	for (const pathHex of path) {
		const distance = hexagonalDistance(row, col, pathHex.row, pathHex.col);
		if (distance < minDistance) {
			minDistance = distance;
		}
	}
	return minDistance;
}

// Calculate distance between two positions (hexagonal grid distance)
function distanceBetween(
	row1: number,
	col1: number,
	row2: number,
	col2: number,
): number {
	return hexagonalDistance(row1, col1, row2, col2);
}

type PathImageConfig = {
	images: Array<string | HexagonImageSrc | StopIcon>;
	startIndex?: number; // Default: 0
	stopIndex?: number; // Default: path.length - 1
	spacing?: number; // Default: 1 (every hexagon)
	/** Optional i18n / marketing overrides keyed by `StopIcon.type` (e.g. `learn`, `boss-battle`). */
	stopCopy?: Partial<Record<string, { title: string; description: string }>>;
};

function mergePathStopCopy(
	stop: StopIcon,
	copy?: Partial<Record<string, { title: string; description: string }>>,
): StopIcon {
	const o = copy?.[stop.type];
	if (!o) {
		return stop;
	}
	return { ...stop, title: o.title, description: o.description };
}

type EnvironmentImageConfig = {
	image: string | HexagonImageSrc;
	count?: number; // Number of instances to place (deprecated, use minCount/maxCount)
	minCount?: number; // Minimum number of instances to place
	maxCount?: number; // Maximum number of instances to place
	minDistanceToPath?: number; // Minimum distance from path (default: 0)
	maxDistanceToPath?: number; // Maximum distance from path (default: Infinity)
	minRow?: number; // Minimum row index (default: 0)
	maxRow?: number; // Maximum row index (default: gridDimensions.rows - 1)
	minCol?: number; // Minimum column index (default: 0)
	maxCol?: number; // Maximum column index (default: gridDimensions.columns - 1)
	minSpacing?: number; // Minimum distance between environment images of the same type (default: 0)
};

type TopicInfo = {
	title: string;
	description: string;
	hasBeenRead?: boolean;
	readDuration?: number;
	readProgress?: number;
};

type TopicConfig = {
	minRow: number; // Minimum row index
	maxRow: number; // Maximum row index
	minCol: number; // Minimum column index
	maxCol: number; // Maximum column index
	minDistanceToPath: number; // Minimum distance from path
	maxDistanceToPath: number; // Maximum distance from path
	minSpacing: number; // Minimum distance between interactables
	hoverDuration?: number; // Hover duration before opening viewbox in milliseconds (default: 1000ms = 1 second)
	autoPlay?: boolean; // If true, auto-plays the viewbox when opened (default: true)
};

type TopicsConfig = {
	topicInfo: Array<TopicInfo>; // Array of topic info (title, description) - count is derived from length
	topicConfig?: TopicConfig;
};

type PlayerIcons = {
	upRight?: string | HexagonImageSrc;
	right?: string | HexagonImageSrc;
	downRight?: string | HexagonImageSrc;
	upLeft?: string | HexagonImageSrc;
	left?: string | HexagonImageSrc;
	downLeft?: string | HexagonImageSrc;
};

type HexagonBackgroundProps = React.ComponentProps<"div"> & {
	children?: React.ReactNode;
	hexagonProps?: React.ComponentProps<"div">;
	hexagonSize?: number; // value greater than 50
	hexagonMargin?: number;
	hoveredHexagon?: { row: number; col: number } | null;
	selectedHexagon?: { row: number; col: number } | null;
	isTranslucent?: boolean; // if true, applies lower opacity to all backgrounds
	isClickable?: boolean; // if true, shows FlagMarker on click
	path?: PathConfig;
	pathImages?: PathImageConfig;
	playerImage?: string | HexagonImageSrc; // Image to place on first hexagon of path (deprecated, use playerIcons instead)
	playerIcons?: PlayerIcons; // Icons for different directions - if provided, will override playerImage
	environmentImages?: Array<EnvironmentImageConfig>;
	topics?: TopicsConfig; // Configuration for topics (separate from environment images)
	featureIndex?: number; // Index of the current feature/tabpanel (0-based)
	totalFeatures?: number; // Total number of features/tabpanels
	onTopicHovered?: (featureIndex: number, topicIndex: number) => void; // Callback when a topic is hovered for the first time
	isActive?: boolean; // If true, shows player image/icons (only visible in active tabpanel)
};

// Lazy-loaded flag marker component
const FlagMarker = React.memo(
	({
		selectedHexagon,
		hoveredHexagon,
		hexagonWidth,
		hexagonHeight,
		hexagonMargin,
		computedMarginTop,
		oddRowMarginLeft,
		evenRowMarginLeft,
		rowSpacing,
	}: {
		selectedHexagon: { row: number; col: number };
		hoveredHexagon?: { row: number; col: number } | null;
		hexagonWidth: number;
		hexagonHeight: number;
		hexagonMargin: number;
		computedMarginTop: number;
		oddRowMarginLeft: number;
		evenRowMarginLeft: number;
		rowSpacing: number;
	}) => {
		const [stopImage, setStopImage] = React.useState<string | null>(null);
		const [isVisible, setIsVisible] = React.useState(false);

		// Lazy load a random stop icon only when component mounts
		React.useEffect(() => {
			// Simulate dynamic/lazy import pattern delay for generality:
			const randomIndex = Math.floor(Math.random() * STOP_ICONS.length);
			const selected = STOP_ICONS[randomIndex];
			// Simulate as if from import, real-world would not require .default if ESM
			const imageSrc =
				typeof selected.icon === "string"
					? selected.icon
					: (selected.icon as any).src || selected.icon;
			setStopImage(imageSrc);

			// Use double requestAnimationFrame to ensure initial state is rendered first
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					setIsVisible(true);
				});
			});
		}, []);

		// Calculate position of selected hexagon
		const rowIndex = selectedHexagon.row;
		const colIndex = selectedHexagon.col;
		const isEvenRow = (rowIndex + 1) % 2 === 0;
		const rowLeftOffset = isEvenRow
			? evenRowMarginLeft - 10
			: oddRowMarginLeft - 10;

		// Calculate top position: row top + half hexagon height to center vertically
		const rowTop = computedMarginTop + rowIndex * rowSpacing * 1.075;
		const top = rowTop + hexagonHeight / 2;
		const left =
			rowLeftOffset +
			colIndex * (hexagonWidth + hexagonMargin) +
			hexagonWidth / 2;

		// Check if this hexagon is hovered
		const isHovered =
			hoveredHexagon?.row === rowIndex &&
			hoveredHexagon?.col === colIndex;

		// Calculate FlagMarker scale: 1.2x when hovered, 1x when visible but not hovered, 0.5x when not visible
		const scale = !isVisible ? 0.5 : isHovered ? 1.2 : 1;

		// Always render the container, even if image hasn't loaded yet
		// This ensures the initial state (scale 0.5, opacity 0) is rendered
		return (
			<div
				className="absolute flex items-center justify-center pointer-events-none"
				style={{
					top: `${top}px`,
					left: `${left + 2}px`,
					transform: `translate(-50%, -50%) scale(${scale})`,
					zIndex: 100,
					opacity: isVisible ? 1 : 0,
					transition:
						"opacity 0.25s cubic-bezier(.4,2,.35,1), transform 0.25s cubic-bezier(.4,2,.35,1)",
				}}
			>
				{stopImage && (
					<img
						src={stopImage}
						alt="Stop Icon"
						className="w-8 h-8"
						style={{
							filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.10))",
						}}
					/>
				)}
			</div>
		);
	},
);

FlagMarker.displayName = "FlagMarker";

// Function to determine tooltip placement based on grid position
function getTooltipPlacement(
	row: number,
	col: number,
	maxRow: number,
	maxCol: number,
): string {
	const xThreshold = 5; // Threshold for edges and corners
	const yThreshold = 3; // Threshold for edges and corners

	// Determine region
	const isTop = row < yThreshold;
	const isBottom = row >= maxRow - yThreshold;
	const isLeft = col < xThreshold;
	const isRight = col >= maxCol - xThreshold;
	const isMiddleRow = !isTop && !isBottom;
	const isMiddleCol = !isLeft && !isRight;

	// Determine placement based on region (opposite of location)
	if (isTop && isLeft) {
		return "bottom-start"; // Top-left → bottom-right
	}
	if (isTop && isRight) {
		return "bottom-end"; // Top-right → bottom-left
	}
	if (isTop && isMiddleCol) {
		return "bottom"; // Top-middle → bottom
	}
	if (isBottom && isLeft) {
		return "top-start"; // Bottom-left → top-right
	}
	if (isBottom && isRight) {
		return "top-end"; // Bottom-right → top-left
	}
	if (isBottom && isMiddleCol) {
		return "top"; // Bottom-middle → top
	}
	if (isMiddleRow && isLeft) {
		return "right"; // Left-middle → right
	}
	if (isMiddleRow && isRight) {
		return "left"; // Right-middle → left
	}
	return "top"; // Middle → top (default)
}

function radixTooltipFromPlacement(placement: string): {
	side: "top" | "bottom" | "left" | "right";
	align: "start" | "center" | "end";
} {
	const parts = placement.split("-");
	const rawSide = parts[0];
	const side =
		rawSide === "top" ||
		rawSide === "bottom" ||
		rawSide === "left" ||
		rawSide === "right"
			? rawSide
			: "top";
	const rawAlign = parts[1];
	const align =
		rawAlign === "start" || rawAlign === "end" ? rawAlign : "center";
	return { side, align };
}

// Component to render an image at a specific hexagon position
const HexagonImageOverlay = React.memo(
	({
		row,
		col,
		image,
		hexagonWidth,
		hexagonHeight,
		hexagonMargin,
		computedMarginTop,
		oddRowMarginLeft,
		evenRowMarginLeft,
		rowSpacing,
		size = "w-8 h-8",
		hoveredHexagon,
		enableHoverScale = false,
		stop,
		pulseDelay,
		isInteractable = false,
		onHover,
		onTransientGridHover,
		topicIndex,
		topicInfo,
		hoverDuration,
		gridDimensions,
		isPlayer = false,
		featureIndex,
		flipHorizontal = false,
		applyLeftOffset = false,
	}: {
		row: number;
		col: number;
		image: string | HexagonImageSrc;
		hexagonWidth: number;
		hexagonHeight: number;
		hexagonMargin: number;
		computedMarginTop: number;
		oddRowMarginLeft: number;
		evenRowMarginLeft: number;
		rowSpacing: number;
		size?: string;
		hoveredHexagon?: { row: number; col: number } | null;
		enableHoverScale?: boolean;
		stop?: StopIcon;
		pulseDelay?: number;
		isInteractable?: boolean;
		onHover?: (row: number, col: number) => void;
		/** Immediate hover cell for grid trail (uncontrolled `hoveredHexagon` hosts). */
		onTransientGridHover?: (
			pos: { row: number; col: number } | null,
		) => void;
		topicIndex?: number;
		topicInfo?: TopicInfo;
		hoverDuration?: number; // Hover duration before opening viewbox in milliseconds (default: 1000ms = 1 second)
		gridDimensions?: { rows: number; columns: number };
		isPlayer?: boolean; // If true, this is the player icon and should appear above stop icons
		featureIndex?: number; // Index of the current feature/tabpanel (0-based)
		flipHorizontal?: boolean; // If true, flip the image horizontally
		applyLeftOffset?: boolean; // If true, apply 2px left offset for centering (independent of hover scale)
	}) => {
		const isEvenRow = (row + 1) % 2 === 0;
		const rowLeftOffset = isEvenRow
			? evenRowMarginLeft - 10
			: oddRowMarginLeft - 10;

		const rowTop = computedMarginTop + row * rowSpacing * 1.075;
		const top = rowTop + hexagonHeight / 2;
		const left =
			rowLeftOffset +
			col * (hexagonWidth + hexagonMargin) +
			hexagonWidth / 2;

		// Check if this hexagon is hovered (only if enableHoverScale or isInteractable is true)
		const isHovered =
			(enableHoverScale || isInteractable) &&
			hoveredHexagon?.row === row &&
			hoveredHexagon?.col === col;

		// Calculate Stop Icon & Interactable scale: 1.5x when hovered, 1x when not hovered
		const scale = isHovered ? 1.5 : 1;

		// Apply left offset of +2px for centering (like FlagMarker) - always apply for interactables or when explicitly requested
		const leftOffset =
			enableHoverScale || isInteractable || applyLeftOffset ? 2 : 0;

		const imageSrc =
			typeof image === "string" ? image : (image as any).src || image;

		// For interactables, check if it has been read to show check circle instead
		const hasBeenRead = isInteractable && topicInfo?.hasBeenRead;

		// Track if interactable is currently being hovered
		const [isCurrentlyHovering, setIsCurrentlyHovering] =
			React.useState(false);

		// Track hover progress for animated circle (0-100)
		const [hoverProgress, setHoverProgress] = React.useState(0);

		// Hover timeout to open viewbox after 1 second
		const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
		const hoverStartTimeRef = React.useRef<number | null>(null);
		const hoverProgressIntervalRef = React.useRef<NodeJS.Timeout | null>(
			null,
		);
		const containerRef = React.useRef<HTMLDivElement>(null);
		const totalHoverDuration = hoverDuration ?? 1000; // Default 1 second to open viewbox

		// Function to trigger confetti from the container position
		const _triggerConfetti = React.useCallback(async () => {
			if (!containerRef.current) {
				return;
			}
			try {
				const rect = containerRef.current.getBoundingClientRect();
				const x = rect.left + rect.width / 2;
				const y = rect.top + rect.height / 2;
				await confetti({
					origin: {
						x: x / window.innerWidth,
						y: y / window.innerHeight,
					},
				});
			} catch {
				/* ignore confetti failures */
			}
		}, []);

		// Handle hover to open viewbox after 1 second with progress tracking
		React.useEffect(() => {
			// Clear any existing timeout and interval
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
				hoverTimeoutRef.current = null;
			}
			if (hoverProgressIntervalRef.current) {
				clearInterval(hoverProgressIntervalRef.current);
				hoverProgressIntervalRef.current = null;
			}

			if (isInteractable && isCurrentlyHovering && onHover) {
				// Reset progress
				setHoverProgress(0);
				hoverStartTimeRef.current = Date.now();

				// Update progress every 10ms (1% increments for 1000ms duration)
				hoverProgressIntervalRef.current = setInterval(() => {
					if (hoverStartTimeRef.current !== null) {
						const elapsed = Date.now() - hoverStartTimeRef.current;
						const progress = Math.min(
							100,
							(elapsed / totalHoverDuration) * 100,
						);
						setHoverProgress(progress);
					}
				}, 10);

				// Start timer - if hover continues for 1 second, open viewbox
				hoverTimeoutRef.current = setTimeout(() => {
					if (hoverProgressIntervalRef.current) {
						clearInterval(hoverProgressIntervalRef.current);
						hoverProgressIntervalRef.current = null;
					}
					setHoverProgress(100);
					onHover(row, col);
					hoverTimeoutRef.current = null;
					hoverStartTimeRef.current = null;
				}, totalHoverDuration);
			} else {
				// Reset progress when not hovering
				setHoverProgress(0);
				hoverStartTimeRef.current = null;
			}

			return () => {
				if (hoverTimeoutRef.current) {
					clearTimeout(hoverTimeoutRef.current);
					hoverTimeoutRef.current = null;
				}
				if (hoverProgressIntervalRef.current) {
					clearInterval(hoverProgressIntervalRef.current);
					hoverProgressIntervalRef.current = null;
				}
			};
		}, [
			isInteractable,
			isCurrentlyHovering,
			hasBeenRead,
			totalHoverDuration,
			onHover,
			row,
			col,
		]);

		const imageElement = (
			<img
				src={imageSrc}
				alt=""
				className={cn(size, "max-w-none shrink-0 object-contain")}
				style={{
					filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.10))",
					transform: flipHorizontal ? "scaleX(-1)" : undefined,
					...(isInteractable &&
						!hasBeenRead &&
						!isCurrentlyHovering && {
							animation:
								"interactable-pulse 2s ease-in-out infinite",
						}),
				}}
			/>
		);

		const key =
			isInteractable &&
			topicIndex !== undefined &&
			featureIndex !== undefined
				? `${featureIndex}-${topicIndex}`
				: undefined;

		const container = (
			<div
				ref={containerRef}
				className={cn(
					"absolute flex shrink-0 items-center justify-center",
					stop || isInteractable
						? "pointer-events-auto"
						: "pointer-events-none",
				)}
				data-topic={key}
				style={{
					top: `${top}px`,
					left: `${left + leftOffset}px`,
					transform: `translate(-50%, -50%) scale(${scale})`,
					zIndex: isPlayer ? 200 : 100, // Player appears above stop icons (z-index 100)
					transition:
						enableHoverScale || isInteractable
							? "opacity 0.25s cubic-bezier(.4,2,.35,1), transform 0.25s cubic-bezier(.4,2,.35,1)"
							: undefined,
				}}
				onMouseEnter={() => {
					if (isInteractable || stop) {
						onTransientGridHover?.({ row, col });
					}
					if (isInteractable) {
						setIsCurrentlyHovering(true);
					}
				}}
				onMouseLeave={() => {
					if (isInteractable || stop) {
						onTransientGridHover?.(null);
					}
					if (isInteractable) {
						setIsCurrentlyHovering(false);
					}
				}}
			>
				{imageElement}
				{isInteractable ? (
					<>
						{/* PulseRing - hides when hovering or has been hovered */}
						<div
							className="absolute inset-0 flex items-center justify-center"
							style={{
								opacity:
									isCurrentlyHovering || hasBeenRead ? 0 : 1,
								transition:
									"opacity 0.3s cubic-bezier(.4,2,.35,1)",
								pointerEvents: "none",
								zIndex: 1,
							}}
						>
							<PulseRing
								delay={pulseDelay ?? Math.random() * 2}
							/>
						</div>

						{/* AnimatedCircularProgressBar - shows hover progress (even if already read) */}
						<div
							className={cn(
								"absolute inset-0 flex items-center justify-center",
								isCurrentlyHovering ? "" : "hidden",
							)}
							style={{
								pointerEvents: "none",
								zIndex: 5,
							}}
						>
							<AnimatedCircularProgressBar
								value={hoverProgress}
								max={100}
								min={0}
								gaugePrimaryColor="#84cc16"
								gaugeSecondaryColor="rgba(255, 255, 255, 0.2)"
								className="text-white text-[0.6rem] font-black"
								transitionLength={10}
								textVisible={!hasBeenRead}
							/>
						</div>

						{/* Interactable index number - hides when hovering or completed */}
						{topicIndex !== undefined && (
							<div
								className="absolute inset-0 flex items-center justify-center"
								style={{
									opacity:
										isCurrentlyHovering || hasBeenRead
											? 0
											: 1,
									transition:
										"opacity 0.3s cubic-bezier(.4,2,.35,1)",
									pointerEvents: "none",
									zIndex: 8,
								}}
							>
								<span className="text-white text-xs font-black">
									{topicIndex + 1}
								</span>
							</div>
						)}

						{/* CheckCircleIcon - fades in and scales from 0 to 1 when read */}
						<CheckCircleIcon
							className="absolute h-8 w-8 text-white"
							style={{
								opacity: hasBeenRead ? 1 : 0,
								transform: `scale(${hasBeenRead ? 1 : 0})`,
								transformOrigin: "center",
								transition:
									"opacity 0.3s cubic-bezier(.4,2,.35,1), transform 0.3s cubic-bezier(.4,2,.35,1)",
								filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.10))",
								pointerEvents: "none",
								zIndex: 10,
							}}
						/>
					</>
				) : null}
			</div>
		);

		// Calculate tooltip placement based on grid position
		const tooltipPlacement = gridDimensions
			? getTooltipPlacement(
					row,
					col,
					gridDimensions.rows,
					gridDimensions.columns,
				)
			: "top";

		if (stop) {
			const { side, align } = radixTooltipFromPlacement(tooltipPlacement);
			return (
				<ContentTooltip
					{...CONTENT_TOOLTIP_SHORT}
					side={side}
					align={align}
					contentClassName={cn(
						"max-w-[200px] border-0 bg-white px-3 py-2 text-center font-black text-[12px] uppercase tracking-wide text-[#334155] shadow-sm",
					)}
					content={
						<>
							<span className="block uppercase">{stop.title}</span>
							<span className="mt-1 block font-light normal-case">
								{stop.description}
							</span>
						</>
					}
				>
					{container}
				</ContentTooltip>
			);
		}

		return container;
	},
);

HexagonImageOverlay.displayName = "HexagonImageOverlay";

// Memoized hexagon component to prevent unnecessary re-renders
const Hexagon = React.memo(
	({
		rowIndex,
		colIndex,
		hexagonWidth,
		hexagonHeight,
		hexagonMargin,
		isHovered,
		isSelected,
		isInTrail,
		isPath,
		hexagonProps,
		isTranslucent,
		isCompleted,
		isPlayerHexagon,
	}: {
		rowIndex: number;
		colIndex: number;
		hexagonWidth: number;
		hexagonHeight: number;
		hexagonMargin: number;
		isHovered: boolean;
		isSelected: boolean;
		isInTrail: boolean;
		isPath: boolean;
		hexagonProps?: React.ComponentProps<"div">;
		isTranslucent?: boolean;
		pathIndex?: number;
		isCompleted?: boolean;
		isPlayerHexagon?: boolean;
	}) => {
		// Track if hexagon is being clicked (mousedown)
		const [isClicked, setIsClicked] = React.useState(false);

		// Determine if this hexagon should be scaled (hovered or player hexagon)
		const shouldScale = isHovered || isPlayerHexagon;

		// Calculate scale: if hovered and clicked, scale to 0.95; if just hovered, scale to 1.5; otherwise 1
		const getScale = () => {
			if (isHovered && isClicked) {
				return 0.95;
			}
			if (shouldScale) {
				return 1.5;
			}
			return 1;
		};

		const scale = getScale();

		return (
			<div
				key={`hexagon-${rowIndex}-${colIndex}`}
				{...hexagonProps}
				onMouseDown={() => {
					if (isHovered) {
						setIsClicked(true); // not working
					}
				}}
				onMouseUp={() => {
					setIsClicked(false);
				}}
				onMouseLeave={() => {
					setIsClicked(false);
				}}
				style={{
					width: hexagonWidth,
					height: hexagonHeight,
					marginLeft: hexagonMargin,
					transform: `scale(${scale})`,
					transition: "transform 0.25s cubic-bezier(.4,2,.35,1)",
					transformOrigin: "50% 50%",
					...(shouldScale ? { zIndex: 10 } : {}),
					...hexagonProps?.style,
				}}
				className={cn(
					"relative",
					"[clip-path:polygon(50%_0%,_100%_25%,_100%_75%,_50%_100%,_0%_75%,_0%_25%)]",
					"before:content-[''] before:absolute before:top-0 before:left-0 before:w-full before:h-full before:opacity-100 before:transition-all",
					isTranslucent
						? "dark:before:bg-neutral-950/5 before:bg-white/5"
						: "dark:before:bg-neutral-950 before:bg-white",
					isSelected
						? "after:content-[''] after:absolute after:inset-[var(--hexagon-margin)] after:bg-white/50"
						: isTranslucent
							? "after:content-[''] after:absolute after:inset-[var(--hexagon-margin)] dark:after:bg-neutral-950/10 after:bg-white/10"
							: "after:content-[''] after:absolute after:inset-[var(--hexagon-margin)] dark:after:bg-neutral-950 after:bg-white",
					"after:[clip-path:polygon(50%_0%,_100%_25%,_100%_75%,_50%_100%,_0%_75%,_0%_25%)]",
					// Added isHovered && isPath condition before other cases
					isHovered && isPath
						? isTranslucent
							? "before:bg-yellow-800/80 dark:before:bg-yellow-400/60 before:opacity-100 before:duration-0 dark:after:bg-yellow-600/70 after:bg-yellow-300/70 after:opacity-100 after:duration-0"
							: "before:bg-yellow-300 dark:before:bg-yellow-400 before:opacity-100 before:duration-0 dark:after:bg-yellow-200 after:bg-yellow-300 after:opacity-100 after:duration-0"
						: isCompleted && isPath
							? isTranslucent
								? "before:bg-lime-800/80 dark:before:bg-lime-400/60 before:opacity-100 before:duration-0 dark:after:bg-lime-600/70 after:bg-lime-300/70 after:opacity-100 after:duration-0"
								: "before:bg-lime-300 dark:before:bg-lime-400 before:opacity-100 before:duration-0 dark:after:bg-lime-200 after:bg-lime-300 after:opacity-100 after:duration-0"
							: isSelected && isPath
								? isTranslucent
									? "before:bg-white/70 dark:before:bg-[color:var(--marketing-surface)] before:opacity-100 before:duration-0 dark:after:bg-amber-400/40 after:bg-white/70 after:opacity-100 after:duration-0"
									: "before:bg-yellow dark:before:bg-yellow-500 before:opacity-100 before:duration-0 dark:after:bg-yellow-600 after:bg-yellow-300 after:opacity-100 after:duration-0"
								: isInTrail && isPath
									? isTranslucent
										? "before:bg-white/70 dark:before:bg-[color:var(--marketing-surface)] before:opacity-100 before:duration-0 dark:after:bg-[color:var(--marketing-surface)] after:bg-white/70 after:opacity-100 after:duration-0"
										: "before:bg-yellow dark:before:bg-yellow-500 before:opacity-100 before:duration-0 dark:after:bg-[color:var(--marketing-surface)] after:bg-white after:opacity-100 after:duration-0"
									: isInTrail
										? isTranslucent
											? "before:bg-gray-300/20 dark:before:bg-gray-800/20 before:opacity-100 before:duration-0 dark:after:bg-gray-900/20 after:bg-gray-100/20 after:opacity-100 after:duration-0"
											: "before:bg-gray-300 dark:before:bg-gray-800 before:opacity-100 before:duration-0 dark:after:bg-gray-900 after:bg-gray-100 after:opacity-100 after:duration-0"
										: isHovered
											? isTranslucent
												? "before:bg-white/50 dark:before:bg-gray-700/50 before:opacity-100 before:duration-0 dark:after:bg-gray-800/50 after:bg-white/50 after:opacity-100 after:duration-0"
												: "before:bg-gray-400 dark:before:bg-gray-700 before:opacity-100 before:duration-0 dark:after:bg-gray-800 after:bg-white after:opacity-100 after:duration-0"
											: isPath
												? isTranslucent
													? "before:bg-white/70 dark:before:bg-[color:var(--marketing-surface)] before:opacity-100 before:duration-0 dark:after:bg-[color:var(--marketing-surface)] after:bg-white/70 after:opacity-100 after:duration-0"
													: "before:bg-white dark:before:bg-[color:var(--marketing-surface)] before:opacity-100 before:duration-0 dark:after:bg-gray-700 after:bg-yellow-400 after:opacity-100 after:duration-0"
												: "before:duration-600",
					// Do not add `hover:before:*` here: it overrides the `isHovered` / path styles on
					// empty cells where the cursor hits the hex div (translucent mode looked washed out).
					hexagonProps?.className,
				)}
			>
				{/* Debug: Show coordinates and path index
      <div
        className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-20"
        style={{
          fontSize: `${Math.max(8, hexagonWidth / 12)}px`,
          fontWeight: 'bold',
          color: isPath ? '#ffffff' : (isTranslucent ? '#000000' : '#000000'),
          textShadow: '0 0 2px rgba(255,255,255,0.8), 0 0 2px rgba(255,255,255,0.8)',
        }}
      >
        <span>{rowIndex},{colIndex}</span>
        {pathIndex !== undefined && (
          <span style={{ fontSize: `${Math.max(10, hexagonWidth / 8)}px`, marginTop: '2px' }}>
            [{pathIndex}]
          </span>
        )}
      </div>*/}
			</div>
		);
	},
	(prevProps, nextProps) => {
		// Custom comparison function for React.memo
		return (
			prevProps.rowIndex === nextProps.rowIndex &&
			prevProps.colIndex === nextProps.colIndex &&
			prevProps.isHovered === nextProps.isHovered &&
			prevProps.isSelected === nextProps.isSelected &&
			prevProps.isInTrail === nextProps.isInTrail &&
			prevProps.isPath === nextProps.isPath &&
			prevProps.pathIndex === nextProps.pathIndex &&
			prevProps.hexagonWidth === nextProps.hexagonWidth &&
			prevProps.hexagonHeight === nextProps.hexagonHeight &&
			prevProps.hexagonMargin === nextProps.hexagonMargin &&
			prevProps.isTranslucent === nextProps.isTranslucent &&
			prevProps.isCompleted === nextProps.isCompleted &&
			prevProps.isPlayerHexagon === nextProps.isPlayerHexagon
		);
	},
);

Hexagon.displayName = "Hexagon";

function HexagonBackground({
	className,
	children,
	hexagonProps,
	hexagonSize = 75,
	hexagonMargin = 3,
	hoveredHexagon,
	selectedHexagon,
	isTranslucent = false,
	isClickable = false,
	path,
	pathImages,
	playerImage,
	playerIcons,
	environmentImages,
	topics,
	featureIndex,
	totalFeatures,
	onTopicHovered,
	isActive = true,
	...props
}: HexagonBackgroundProps) {
	const hexagonWidth = hexagonSize;
	const hexagonHeight = hexagonSize * 1.1;
	const rowSpacing = hexagonSize * 0.8;
	const baseMarginTop = -36 - 0.275 * (hexagonSize - 100);
	const computedMarginTop = baseMarginTop + hexagonMargin;
	const oddRowMarginLeft = -(hexagonSize / 2);
	const evenRowMarginLeft = hexagonMargin / 2;

	const [gridDimensions, setGridDimensions] = React.useState({
		rows: 0,
		columns: 0,
	});

	const [isMounted, setIsMounted] = React.useState(false);

	const [internalHoveredHexagon, setInternalHoveredHexagon] = React.useState<{
		row: number;
		col: number;
	} | null>(null);
	/** When `hoveredHexagon` is controlled, path/stop overlays still need to drive hover/trail (pointer-events on icons). */
	const [overlayHoverAssist, setOverlayHoverAssist] = React.useState<{
		row: number;
		col: number;
	} | null>(null);

	const resolvedHoveredHexagon = React.useMemo(() => {
		if (hoveredHexagon === undefined) {
			return internalHoveredHexagon;
		}
		if (overlayHoverAssist != null) {
			return overlayHoverAssist;
		}
		return hoveredHexagon;
	}, [hoveredHexagon, internalHoveredHexagon, overlayHoverAssist]);

	// State for player position (starts at index 1)
	const [playerPathIndex, setPlayerPathIndex] = React.useState(1);

	// Ref for the container element
	const containerRef = React.useRef<HTMLDivElement>(null);

	// Cache generated paths per featureIndex to prevent recalculation when switching tabpanels
	const pathCacheRef = React.useRef<
		Map<
			string,
			{
				pathSet: Set<string>;
				pathIndexMap: Map<string, number>;
				generatedPath: Array<{ row: number; col: number }>;
			}
		>
	>(new Map());

	// Cache topic and environment positions per featureIndex to prevent recalculation
	const topicPositionsCacheRef = React.useRef<
		Map<
			string,
			Array<{
				row: number;
				col: number;
				pulseDelay: number;
				hoverDuration?: number;
			}>
		>
	>(new Map());
	const environmentPositionsCacheRef = React.useRef<
		Map<
			string,
			Array<{
				row: number;
				col: number;
				image: string | HexagonImageSrc;
				pulseDelay?: number;
				flipHorizontal?: boolean;
			}>
		>
	>(new Map());

	// Memoize grid dimensions calculation based on container size
	const calculateGridDimensions = React.useCallback(() => {
		if (typeof window === "undefined" || !containerRef.current) {
			return { rows: 0, columns: 0 };
		}
		const container = containerRef.current;
		const rows = Math.ceil(container.clientHeight / rowSpacing);
		const cellPitchX = hexagonWidth + hexagonMargin;
		// +1 column: clip-path hexes and row offsets leave a sliver on the right; extra cells fill it
		// (marketing solution demo, hero, and any host — path/layout widths unchanged).
		const columns = Math.ceil(container.clientWidth / cellPitchX) + 1;
		return { rows, columns };
	}, [rowSpacing, hexagonWidth, hexagonMargin]);

	// Debounced resize handler
	const updateGridDimensions = React.useCallback(() => {
		const dimensions = calculateGridDimensions();
		setGridDimensions(dimensions);
	}, [calculateGridDimensions]);

	React.useEffect(() => {
		// Mark as mounted on client
		setIsMounted(true);

		if (!containerRef.current) {
			return;
		}

		// Initial calculation
		updateGridDimensions();

		// Debounce resize events
		let timeoutId: NodeJS.Timeout;

		// Use ResizeObserver to watch container size changes
		const resizeObserver = new ResizeObserver(() => {
			clearTimeout(timeoutId);
			timeoutId = setTimeout(updateGridDimensions, 150);
		});

		resizeObserver.observe(containerRef.current);

		return () => {
			resizeObserver.disconnect();
			clearTimeout(timeoutId);
		};
	}, [updateGridDimensions]);

	// Generate path if path config is provided
	// Cache per featureIndex and refreshKey to prevent recalculation when switching tabpanels
	// Only generate on client to avoid hydration mismatch
	const { pathSet, pathIndexMap, generatedPath } = React.useMemo(() => {
		if (!path || !isMounted) {
			return {
				pathSet: new Set<string>(),
				pathIndexMap: new Map<string, number>(),
				generatedPath: [] as Array<{ row: number; col: number }>,
			};
		}

		// Create cache key based on featureIndex and path config (excluding refreshKey for stability)
		const cacheKey = `${featureIndex ?? "default"}-${path.startRow ?? 3}-${path.startCol ?? 0}-${path.endRow ?? 3}-${path.endCol ?? 10}-${path.yMinRange ?? -2}-${path.yMaxRange ?? 2}-${path.xMaxStepsBack ?? 1}-${path.xMaxStepsForward ?? 3}-${path.refreshKey ?? 0}`;

		// Check cache first
		const cached = pathCacheRef.current.get(cacheKey);
		if (cached) {
			return cached;
		}

		const pathConfig: Required<Omit<PathConfig, "refreshKey">> = {
			startRow: path.startRow ?? 3,
			startCol: path.startCol ?? 0,
			endRow: path.endRow ?? 3,
			endCol: path.endCol ?? 10,
			yMinRange: path.yMinRange ?? -2,
			yMaxRange: path.yMaxRange ?? 2,
			xMaxStepsBack: path.xMaxStepsBack ?? 1,
			xMaxStepsForward: path.xMaxStepsForward ?? 3,
		};

		const generatedPath = generatePath(pathConfig);
		// Safely create pathSet
		const pathSet = new Set<string>();
		if (generatedPath != null && Array.isArray(generatedPath)) {
			for (let i = 0; i < generatedPath.length; i++) {
				const p = generatedPath[i];
				if (
					p != null &&
					typeof p === "object" &&
					"row" in p &&
					"col" in p
				) {
					pathSet.add(`${p.row},${p.col}`);
				}
			}
		}
		const pathIndexMap = new Map<string, number>();

		// Create a map from position to path index
		if (generatedPath != null && Array.isArray(generatedPath)) {
			for (let i = 0; i < generatedPath.length; i++) {
				const p = generatedPath[i];
				if (
					p != null &&
					typeof p === "object" &&
					"row" in p &&
					"col" in p
				) {
					pathIndexMap.set(`${p.row},${p.col}`, i);
				}
			}
		}

		const result = { pathSet, pathIndexMap, generatedPath };
		// Cache the result
		pathCacheRef.current.set(cacheKey, result);

		return result;
	}, [
		path?.refreshKey,
		path?.startRow,
		path?.startCol,
		path?.endRow,
		path?.endCol,
		path?.yMinRange,
		path?.yMaxRange,
		path?.xMaxStepsBack,
		path?.xMaxStepsForward,
		isMounted,
		featureIndex,
	]);

	// Reset player position when path changes
	React.useEffect(() => {
		setPlayerPathIndex(1);
	}, [path?.refreshKey]);

	// Pre-calculate distance map for all grid positions to avoid repeated distanceToPath calls
	// This dramatically improves performance when calculating topic/environment positions
	const distanceMap = React.useMemo(() => {
		if (
			!generatedPath ||
			generatedPath.length === 0 ||
			!gridDimensions ||
			gridDimensions.rows === 0 ||
			gridDimensions.columns === 0
		) {
			return new Map<string, number>();
		}

		// Calculate maximum maxDistanceToPath from all sources (topics and environment images)
		// Use safe access with proper null checks to avoid "uninitialized variable" errors
		let maxMaxDistance = 10; // Default fallback

		// Safely access topics config
		if (
			topics != null &&
			typeof topics === "object" &&
			"topicConfig" in topics
		) {
			const topicConfig = topics.topicConfig;
			if (
				topicConfig != null &&
				typeof topicConfig === "object" &&
				"maxDistanceToPath" in topicConfig
			) {
				const maxDist = topicConfig.maxDistanceToPath;
				if (typeof maxDist === "number" && Number.isFinite(maxDist)) {
					maxMaxDistance = Math.max(maxMaxDistance, maxDist);
				}
			}
		}

		// Safely iterate over environmentImages array
		if (
			environmentImages != null &&
			Array.isArray(environmentImages) &&
			environmentImages.length > 0
		) {
			for (let i = 0; i < environmentImages.length; i++) {
				const envConfig = environmentImages[i];
				if (
					envConfig != null &&
					typeof envConfig === "object" &&
					"maxDistanceToPath" in envConfig
				) {
					const envMaxDist = envConfig.maxDistanceToPath;
					if (
						envMaxDist != null &&
						typeof envMaxDist === "number" &&
						envMaxDist !== Number.POSITIVE_INFINITY &&
						Number.isFinite(envMaxDist)
					) {
						maxMaxDistance = Math.max(maxMaxDistance, envMaxDist);
					}
				}
			}
		}

		const map = new Map<string, number>();
		// Only calculate distances for positions within reasonable bounds (path + max distance)
		// This reduces calculations from potentially 1000+ to a smaller subset
		// Dynamically use the maximum maxDistanceToPath from all configs
		// Ensure we have valid path data before calculating bounds
		if (generatedPath.length === 0) {
			return map;
		}

		// Guard against invalid/NaN bounds
		// Ensure generatedPath has valid elements before mapping
		if (!Array.isArray(generatedPath) || generatedPath.length === 0) {
			return map;
		}

		// Safely extract rows and cols, filtering out any invalid entries
		const pathRows: number[] = [];
		const pathCols: number[] = [];

		for (const p of generatedPath) {
			if (p && typeof p === "object" && "row" in p && "col" in p) {
				const row = p.row;
				const col = p.col;
				if (typeof row === "number" && Number.isFinite(row)) {
					pathRows.push(row);
				}
				if (typeof col === "number" && Number.isFinite(col)) {
					pathCols.push(col);
				}
			}
		}

		if (pathRows.length === 0 || pathCols.length === 0) {
			return map;
		}

		const minRowBase = Math.min(...pathRows);
		const maxRowBase = Math.max(...pathRows);
		const minColBase = Math.min(...pathCols);
		const maxColBase = Math.max(...pathCols);

		if (
			!Number.isFinite(minRowBase) ||
			!Number.isFinite(maxRowBase) ||
			!Number.isFinite(minColBase) ||
			!Number.isFinite(maxColBase) ||
			!Number.isFinite(maxMaxDistance)
		) {
			return map;
		}

		const pathBounds = {
			minRow: minRowBase - maxMaxDistance,
			maxRow: maxRowBase + maxMaxDistance,
			minCol: minColBase - maxMaxDistance,
			maxCol: maxColBase + maxMaxDistance,
		};

		// Calculate distances only for positions within bounds
		for (
			let row = Math.max(0, pathBounds.minRow);
			row <= Math.min(gridDimensions.rows - 1, pathBounds.maxRow);
			row++
		) {
			for (
				let col = Math.max(0, pathBounds.minCol);
				col <= Math.min(gridDimensions.columns - 1, pathBounds.maxCol);
				col++
			) {
				const key = `${row},${col}`;
				let minDistance = Number.POSITIVE_INFINITY;
				for (const pathHex of generatedPath) {
					const distance = hexagonalDistance(
						row,
						col,
						pathHex.row,
						pathHex.col,
					);
					if (distance < minDistance) {
						minDistance = distance;
					}
				}
				map.set(key, minDistance);
			}
		}

		return map;
	}, [generatedPath, gridDimensions, topics, environmentImages]);

	// Pre-calculate neighbor sets for hover trail calculation
	const hoveredNeighbors = React.useMemo(() => {
		if (!resolvedHoveredHexagon) {
			return new Set<string>();
		}
		const neighbors = getNeighbors(
			resolvedHoveredHexagon.row,
			resolvedHoveredHexagon.col,
		);
		return new Set(neighbors.map((n) => `${n.row},${n.col}`));
	}, [resolvedHoveredHexagon]);

	const setTransientHoveredCell = React.useCallback(
		(pos: { row: number; col: number } | null) => {
			if (hoveredHexagon === undefined) {
				setInternalHoveredHexagon(pos);
				return;
			}
			setOverlayHoverAssist(pos);
		},
		[hoveredHexagon],
	);

	// Pre-calculate used positions set to avoid recreating it
	// This is used by both pathImagePositions and environmentImagePositions
	const usedPositionsBase = React.useMemo(() => {
		const used = new Set<string>();
		if (
			generatedPath != null &&
			Array.isArray(generatedPath) &&
			generatedPath.length > 0
		) {
			for (let i = 0; i < generatedPath.length; i++) {
				const p = generatedPath[i];
				if (
					p != null &&
					typeof p === "object" &&
					"row" in p &&
					"col" in p
				) {
					used.add(`${p.row},${p.col}`);
				}
			}
			// Exclude player position (first hexagon)
			const firstPath = generatedPath[0];
			if (
				firstPath != null &&
				typeof firstPath === "object" &&
				"row" in firstPath &&
				"col" in firstPath
			) {
				used.add(`${firstPath.row},${firstPath.col}`);
			}
		}
		return used;
	}, [generatedPath]);

	// Generate path images positions
	const pathImagePositions = React.useMemo(() => {
		if (!pathImages || !generatedPath || generatedPath.length === 0) {
			return [];
		}

		const startIndex = pathImages.startIndex ?? 0;
		const stopIndex = pathImages.stopIndex ?? generatedPath.length - 1;
		const spacing = pathImages.spacing ?? 1;
		const images = pathImages.images;
		const stopCopy = pathImages.stopCopy;
		const finalIndex = generatedPath.length - 1;
		const penultimateIndex = generatedPath.length - 2;

		const positions: Array<{
			row: number;
			col: number;
			image: string | HexagonImageSrc;
			stop?: StopIcon;
		}> = [];

		let lastPlacedIndex = startIndex - spacing; // Track last placed index, initialized to allow first placement
		let isFirstStop = true; // Track if this is the first stop being placed
		let lastTwoStopTypes: string[] = []; // Track the last two stop types to prevent more than 2 consecutive

		for (let i = startIndex; i <= stopIndex; i += 1) {
			if (i < generatedPath.length && images.length > 0) {
				// Skip penultimate tile
				if (i === penultimateIndex) {
					continue;
				}

				// Always place boss battle stop on final tile
				if (i === finalIndex) {
					const pathHex = generatedPath[i];
					const bossBattleStop: StopIcon = mergePathStopCopy(
						{
							icon: `${LANDING_IMG}/stop-boss-battle.png`,
							type: "boss-battle",
							title: "Boss Encounter",
							description: "Face the ultimate challenge",
						},
						stopCopy,
					);
					positions.push({
						row: pathHex.row,
						col: pathHex.col,
						image: bossBattleStop.icon,
						stop: bossBattleStop,
					});
					lastPlacedIndex = i;
					// Reset consecutive tracking for boss battle stop (it's excluded from the rule)
					lastTwoStopTypes = [];
					continue;
				}

				// Check if at least spacing tiles have passed since last placement
				if (i - lastPlacedIndex < spacing) {
					continue;
				}

				// Regular path image placement for other tiles
				const pathHex = generatedPath[i];
				let imageIndex =
					Math.floor((i - startIndex) / spacing) % images.length;
				let imageItem = images[imageIndex];
				let attempts = 0;
				const maxAttempts = images.length; // Prevent infinite loop

				// Find a suitable stop that meets our constraints
				while (attempts < maxAttempts) {
					// Check if it's a StopIcon object or just an image
					const isStopIcon =
						typeof imageItem === "object" &&
						imageItem !== null &&
						"icon" in imageItem &&
						"type" in imageItem;

					if (isStopIcon) {
						const stop = imageItem as StopIcon;
						const stopType = stop.type;

						// First stop must be a learn stop
						if (isFirstStop && stopType !== "learn") {
							attempts++;
							imageIndex = (imageIndex + 1) % images.length;
							imageItem = images[imageIndex];
							continue;
						}

						// Check for more than 2 consecutive stops of the same type
						if (
							lastTwoStopTypes.length === 2 &&
							lastTwoStopTypes[0] === stopType &&
							lastTwoStopTypes[1] === stopType
						) {
							// This would be the third consecutive stop of this type, skip it
							attempts++;
							imageIndex = (imageIndex + 1) % images.length;
							imageItem = images[imageIndex];
							continue;
						}

						// This stop is valid, place it
						positions.push({
							row: pathHex.row,
							col: pathHex.col,
							image: stop.icon,
							stop: mergePathStopCopy(stop, stopCopy),
						});
						lastPlacedIndex = i;
						isFirstStop = false;

						// Update consecutive tracking
						if (lastTwoStopTypes.length < 2) {
							lastTwoStopTypes.push(stopType);
						} else {
							lastTwoStopTypes.shift(); // Remove oldest
							lastTwoStopTypes.push(stopType); // Add newest
						}
						break; // Successfully placed, exit the while loop
					}
					// Not a StopIcon, just place it as-is (for non-stop images)
					positions.push({
						row: pathHex.row,
						col: pathHex.col,
						image: imageItem as string | HexagonImageSrc,
					});
					lastPlacedIndex = i;
					isFirstStop = false;
					// Reset consecutive tracking for non-stop images
					lastTwoStopTypes = [];
					break;
				}
			}
		}

		return positions;
	}, [pathImages, generatedPath, path?.refreshKey]);

	// Generate environment images positions
	const environmentImagePositions = React.useMemo(() => {
		if (
			!environmentImages ||
			!generatedPath ||
			generatedPath.length === 0 ||
			!gridDimensions
		) {
			return [];
		}

		// Create cache key based on featureIndex, gridDimensions, path refreshKey, and environmentImages config
		// Include path?.refreshKey so positions update when path is refreshed
		const envCacheKey = `${featureIndex ?? "default"}-${path?.refreshKey ?? 0}-${gridDimensions.rows}-${gridDimensions.columns}-${JSON.stringify(environmentImages.map((e) => ({ minRow: e.minRow, maxRow: e.maxRow, minCol: e.minCol, maxCol: e.maxCol, minDist: e.minDistanceToPath, maxDist: e.maxDistanceToPath, minSpacing: e.minSpacing, count: e.count, minCount: e.minCount, maxCount: e.maxCount })))}`;

		// Check cache first
		const cached = environmentPositionsCacheRef.current.get(envCacheKey);
		if (cached) {
			return cached;
		}

		const positions: Array<{
			row: number;
			col: number;
			image: string | HexagonImageSrc;
			pulseDelay?: number;
			flipHorizontal?: boolean;
		}> = [];
		// Reuse the same usedPositions logic but create a new set for environment-specific exclusions
		// Note: pathImagePositions are already excluded via usedPositionsBase since they're placed on path hexagons
		const envUsedPositions = new Set(usedPositionsBase);

		// Track placed positions per image type for minSpacing checks
		const placedByImageType = new Map<
			string | HexagonImageSrc,
			Array<{ row: number; col: number }>
		>();

		// Safely iterate over environmentImages array
		if (
			environmentImages != null &&
			Array.isArray(environmentImages) &&
			environmentImages.length > 0
		) {
			for (let i = 0; i < environmentImages.length; i++) {
				const envConfig = environmentImages[i];
				if (envConfig == null || typeof envConfig !== "object") {
					continue;
				}
				const minDist = envConfig.minDistanceToPath ?? 0;
				const maxDist =
					envConfig.maxDistanceToPath ?? Number.POSITIVE_INFINITY;
				// Generate random count between minCount and maxCount if provided, otherwise use count
				let count: number;
				if (
					envConfig.minCount !== undefined &&
					envConfig.maxCount !== undefined
				) {
					count =
						Math.floor(
							Math.random() *
								(envConfig.maxCount - envConfig.minCount + 1),
						) + envConfig.minCount;
				} else if (envConfig.count !== undefined) {
					count = envConfig.count;
				} else {
					count = 1; // Default to 1 if neither is provided
				}
				const minSpacing = envConfig.minSpacing ?? 0;

				// Get bounds for row and column constraints
				const minRow = envConfig.minRow ?? 0;
				const maxRow = envConfig.maxRow ?? gridDimensions.rows - 1;
				const minCol = envConfig.minCol ?? 0;
				const maxCol = envConfig.maxCol ?? gridDimensions.columns - 1;

				// Get all non-path hexagons within the grid that meet distance requirements and bounds
				const candidatePositions: Array<{ row: number; col: number }> =
					[];

				// Use pre-calculated distance map for much faster lookups
				for (
					let row = minRow;
					row <= maxRow && row < gridDimensions.rows;
					row++
				) {
					for (
						let col = minCol;
						col <= maxCol && col < gridDimensions.columns;
						col++
					) {
						const key = `${row},${col}`;
						if (!envUsedPositions.has(key)) {
							// Use cached distance from map, fallback to calculation if not cached
							const dist =
								distanceMap.get(key) ??
								distanceToPath(row, col, generatedPath);
							if (dist >= minDist && dist <= maxDist) {
								candidatePositions.push({ row, col });
							}
						}
					}
				}

				// Randomly shuffle candidate positions
				const shuffled = [...candidatePositions].sort(
					() => Math.random() - 0.5,
				);

				// Get already placed positions for this image type
				const imageKey = envConfig.image;
				const alreadyPlaced = placedByImageType.get(imageKey) ?? [];

				// Select positions that meet minSpacing requirement
				const selected: Array<{ row: number; col: number }> = [];
				for (const candidate of shuffled) {
					if (selected.length >= count) {
						break;
					}

					// Check if this position meets minSpacing requirement
					let meetsSpacing = true;
					if (minSpacing > 0) {
						// Check against already placed positions of the same type
						for (const placed of alreadyPlaced) {
							const dist = distanceBetween(
								candidate.row,
								candidate.col,
								placed.row,
								placed.col,
							);
							if (dist < minSpacing) {
								meetsSpacing = false;
								break;
							}
						}

						// Also check against positions being placed in this iteration
						if (meetsSpacing) {
							for (const currentSelected of selected) {
								const dist = distanceBetween(
									candidate.row,
									candidate.col,
									currentSelected.row,
									currentSelected.col,
								);
								if (dist < minSpacing) {
									meetsSpacing = false;
									break;
								}
							}
						}
					}

					if (meetsSpacing) {
						selected.push(candidate);
						envUsedPositions.add(
							`${candidate.row},${candidate.col}`,
						);
					}
				}

				// Update placed positions for this image type
				placedByImageType.set(imageKey, [
					...alreadyPlaced,
					...selected,
				]);

				// Only place images that meet all constraints
				for (let j = 0; j < selected.length; j++) {
					const pos = selected[j];
					if (
						pos != null &&
						typeof pos === "object" &&
						"row" in pos &&
						"col" in pos
					) {
						positions.push({
							row: pos.row,
							col: pos.col,
							image: envConfig.image,
							pulseDelay: Math.random() * 2, // Random delay between 0 and 2 seconds for staggered pulse effect
							flipHorizontal: Math.random() < 0.5, // 50% chance of horizontal flip
						});
					}
				}
			}
		}

		// Cache the result
		environmentPositionsCacheRef.current.set(envCacheKey, positions);

		return positions;
	}, [
		environmentImages,
		generatedPath,
		gridDimensions,
		usedPositionsBase,
		featureIndex,
		path?.refreshKey,
	]);

	// Generate topic positions separately from environment images
	// Extract stable config values to prevent regeneration when topicInfo changes
	const topicConfig = React.useMemo(() => {
		if (!topics) {
			return null;
		}

		// Use nested topicConfig - all config values are in topicConfig
		const config = topics.topicConfig || {
			minRow: 1,
			maxRow: 7,
			minCol: 2,
			maxCol: 14,
			minDistanceToPath: 2,
			maxDistanceToPath: 10,
			minSpacing: 5,
			hoverDuration: 1000,
			autoPlay: true,
		};

		return {
			...config,
		};
	}, [topics?.topicInfo, topics?.topicConfig]);

	// Pre-calculate used positions set for topics (includes path, path images, and environment images)
	// Stable usedPositions for topics - doesn't depend on pathImagePositions (which change with refreshKey)
	// Path images are always on path hexagons, so they're already in usedPositionsBase
	const usedPositionsForTopics = React.useMemo(() => {
		const used = new Set(usedPositionsBase);
		// Safely add environment image positions (these are stable per feature)
		if (
			environmentImagePositions != null &&
			Array.isArray(environmentImagePositions)
		) {
			for (let i = 0; i < environmentImagePositions.length; i++) {
				const pos = environmentImagePositions[i];
				if (
					pos != null &&
					typeof pos === "object" &&
					"row" in pos &&
					"col" in pos
				) {
					used.add(`${pos.row},${pos.col}`);
				}
			}
		}
		return used;
	}, [usedPositionsBase, environmentImagePositions]);

	// Full usedPositions including path images (for other uses that need it)
	const _usedPositions = React.useMemo(() => {
		const used = new Set(usedPositionsForTopics);
		// Safely add path image positions (these change with refreshKey, but topics don't need them)
		if (pathImagePositions != null && Array.isArray(pathImagePositions)) {
			for (let i = 0; i < pathImagePositions.length; i++) {
				const pos = pathImagePositions[i];
				if (
					pos != null &&
					typeof pos === "object" &&
					"row" in pos &&
					"col" in pos
				) {
					used.add(`${pos.row},${pos.col}`);
				}
			}
		}
		return used;
	}, [usedPositionsForTopics, pathImagePositions]);

	const topicPositions = React.useMemo(() => {
		if (
			!topicConfig ||
			!generatedPath ||
			generatedPath.length === 0 ||
			!gridDimensions ||
			!topics?.topicInfo
		) {
			return [];
		}

		// Create cache key based on featureIndex, gridDimensions, path refreshKey, topicConfig, and topic count
		// Include path?.refreshKey so positions update when path is refreshed
		const topicCacheKey = `${featureIndex ?? "default"}-${path?.refreshKey ?? 0}-${gridDimensions.rows}-${gridDimensions.columns}-${topicConfig.minRow}-${topicConfig.maxRow}-${topicConfig.minCol}-${topicConfig.maxCol}-${topicConfig.minDistanceToPath}-${topicConfig.maxDistanceToPath}-${topicConfig.minSpacing}-${topics.topicInfo.length}`;

		// Check cache first
		const cached = topicPositionsCacheRef.current.get(topicCacheKey);
		if (cached) {
			return cached;
		}

		const _positions: Array<{
			row: number;
			col: number;
			pulseDelay: number;
			hoverDuration?: number;
		}> = [];

		const minDist = topicConfig.minDistanceToPath;
		const maxDist = topicConfig.maxDistanceToPath;
		const count = topics?.topicInfo?.length ?? 0;
		const minSpacing = topicConfig.minSpacing;
		const minRow = topicConfig.minRow;
		const maxRow = topicConfig.maxRow;
		const minCol = topicConfig.minCol;
		const maxCol = topicConfig.maxCol;

		// Get all non-path hexagons within the grid that meet distance requirements and bounds
		const candidatePositions: Array<{ row: number; col: number }> = [];

		// Use pre-calculated distance map for much faster lookups
		for (
			let row = minRow;
			row <= maxRow && row < gridDimensions.rows;
			row++
		) {
			for (
				let col = minCol;
				col <= maxCol && col < gridDimensions.columns;
				col++
			) {
				const key = `${row},${col}`;
				if (!usedPositionsForTopics.has(key)) {
					// Use cached distance from map, fallback to calculation if not cached
					const dist =
						distanceMap.get(key) ??
						distanceToPath(row, col, generatedPath);
					if (dist >= minDist && dist <= maxDist) {
						candidatePositions.push({ row, col });
					}
				}
			}
		}

		// Use featureIndex as seed for stable positioning per feature (not path refresh)
		const seed = featureIndex ?? 0;
		const seededRandom = (() => {
			let value = seed * 1000; // Multiply to get better distribution
			return () => {
				value = (value * 9301 + 49297) % 233280;
				return value / 233280;
			};
		})();

		// Randomly shuffle candidate positions using seeded random
		const shuffled = [...candidatePositions].sort(
			() => seededRandom() - 0.5,
		);

		// Select positions that meet minSpacing requirement
		const selected: Array<{ row: number; col: number }> = [];
		for (const candidate of shuffled) {
			if (selected.length >= count) {
				break;
			}

			// Check if this position meets minSpacing requirement
			let meetsSpacing = true;
			if (minSpacing > 0) {
				for (const currentSelected of selected) {
					const dist = distanceBetween(
						candidate.row,
						candidate.col,
						currentSelected.row,
						currentSelected.col,
					);
					if (dist < minSpacing) {
						meetsSpacing = false;
						break;
					}
				}
			}

			if (meetsSpacing) {
				selected.push(candidate);
				// Note: usedPositions is already a Set, but we're creating a new one for topics
				// This is fine as it's scoped to this calculation
			}
		}

		// Fallback: if not enough positions found with spacing, place remaining at random valid positions
		if (selected.length < count) {
			const remainingNeeded = count - selected.length;
			const fallbackCandidates = shuffled.filter((candidate) => {
				const alreadySelected = selected.some(
					(s) => s.row === candidate.row && s.col === candidate.col,
				);
				if (alreadySelected) {
					return false;
				}
				const key = `${candidate.row},${candidate.col}`;
				if (usedPositionsForTopics.has(key)) {
					return false;
				}
				return true;
			});

			const fallbackSelected = fallbackCandidates
				.sort(() => seededRandom() - 0.5)
				.slice(0, remainingNeeded);

			fallbackSelected.forEach((candidate) => {
				selected.push(candidate);
				// Note: usedPositions tracking not needed here as we're done with selection
			});
		}

		// Sort selected positions top-to-bottom, then left-to-right
		// In a hexagon grid, when columns are the same, odd rows appear to the left of even rows
		const sorted = selected.sort((a, b) => {
			if (a.col !== b.col) {
				return a.col - b.col;
			}
			// Same column - check if rows are odd/even
			const aIsOdd = (a.row + 1) % 2 === 1;
			const bIsOdd = (b.row + 1) % 2 === 1;

			// If both are same type (both odd or both even), sort by row
			if (aIsOdd === bIsOdd) {
				return a.row - b.row;
			}

			// If one is odd and one is even, odd comes first (appears to the left)
			return aIsOdd ? -1 : 1;
		});

		// Return positions with metadata
		// Reuse the same seeded random function for pulseDelay to maintain consistency
		const result = sorted.map((pos, _index) => ({
			row: pos.row,
			col: pos.col,
			pulseDelay: seededRandom() * 2,
			hoverDuration: topicConfig.hoverDuration,
		}));

		// Cache the result
		topicPositionsCacheRef.current.set(topicCacheKey, result);

		return result;
	}, [
		topicConfig,
		generatedPath,
		gridDimensions,
		featureIndex,
		topics?.topicInfo,
		distanceMap,
		usedPositionsForTopics,
		path?.refreshKey,
	]);

	// Create topicInfo array with title, description, and hasBeenRead
	// Use topicInfo if available
	const topicInfo = React.useMemo(() => {
		if (!topics || topicPositions.length === 0) {
			return [];
		}

		return topicPositions.map((_, index) => {
			const providedInfo = topics.topicInfo?.[index];
			return {
				title: providedInfo?.title ?? `Topic ${index + 1}`,
				description:
					providedInfo?.description ??
					`This is topic item ${index + 1} of ${topicPositions.length}`,
				hasBeenRead: providedInfo?.hasBeenRead ?? false,
			};
		});
	}, [topics, topicPositions]);

	// Set player position based on hovered interactables count when path refreshes
	React.useEffect(() => {
		if (!generatedPath || generatedPath.length <= 1) {
			return;
		}

		if (topicPositions.length === 0) {
			setPlayerPathIndex(1);
			return;
		}

		// Count how many interactables have been read
		const hoveredCount = topicInfo.filter(
			(info) => info?.hasBeenRead,
		).length;

		// Calculate final position based on hovered count
		// Start at position 1 (first tile after start), then move forward for each topic read
		if (hoveredCount > 0) {
			const totalTopics = topicPositions.length;
			const pathLength = generatedPath.length;
			const moveBy = Math.floor((1 / totalTopics) * (pathLength - 1));
			const finalIndex = Math.min(
				1 + moveBy * hoveredCount,
				pathLength - 2,
			); // Cap at penultimate tile
			setPlayerPathIndex(finalIndex);
		} else {
			setPlayerPathIndex(1);
		}
	}, [path?.refreshKey, generatedPath, topicPositions.length, topicInfo]);

	// Handle interactable hover
	const handleTopicHover = React.useCallback(
		(row: number, col: number) => {
			// Find the topic index
			const topicIndex = topicPositions.findIndex(
				(pos) => pos.row === row && pos.col === col,
			);

			if (topicIndex === -1) {
				return;
			}

			// Notify parent component that this topic viewbox should open
			// Don't check if already read - allow reopening viewbox
			if (onTopicHovered && featureIndex !== undefined) {
				onTopicHovered(featureIndex, topicIndex);
			}

			if (hoveredHexagon === undefined) {
				setInternalHoveredHexagon({ row, col });
			}

			// Don't mark as read or move player here - that happens when viewbox read completes
		},
		[
			topicPositions,
			topicInfo,
			generatedPath,
			featureIndex,
			onTopicHovered,
			hoveredHexagon,
		],
	);

	// Calculate completed path hexagons (up to and including player position) and determine player icon
	const { completedPathSet, playerHexagon, currentPlayerIcon } =
		React.useMemo(() => {
			const hasPlayerIcon = playerIcons || playerImage;
			// Only show player in active tabpanel
			if (
				!isActive ||
				!hasPlayerIcon ||
				!generatedPath ||
				generatedPath.length === 0
			) {
				return {
					completedPathSet: new Set<string>(),
					playerHexagon: null,
					currentPlayerIcon: null,
				};
			}

			// Use the state-based playerPathIndex instead of hardcoded 1
			const currentPlayerPathIndex = Math.min(
				playerPathIndex,
				generatedPath.length - 1,
			);
			const playerHex = generatedPath[currentPlayerPathIndex];

			if (!playerHex) {
				return {
					completedPathSet: new Set<string>(),
					playerHexagon: null,
					currentPlayerIcon: null,
				};
			}

			const playerRow = playerHex.row;
			const playerCol = playerHex.col;

			// Create set of completed path hexagons (all up to and including player position)
			const completedSet = new Set<string>();
			for (
				let i = 0;
				i <= currentPlayerPathIndex && i < generatedPath.length;
				i++
			) {
				const hex = generatedPath[i];
				completedSet.add(`${hex.row},${hex.col}`);
			}

			// Determine player icon based on direction to next hexagon
			let icon: string | HexagonImageSrc | null = null;
			if (playerIcons) {
				// If we have playerIcons, determine direction to next hexagon
				if (currentPlayerPathIndex < generatedPath.length - 1) {
					const nextHex = generatedPath[currentPlayerPathIndex + 1];
					const direction = getDirection(
						playerRow,
						playerCol,
						nextHex.row,
						nextHex.col,
					);
					icon = playerIcons[direction] || playerIcons.right || null;
				} else {
					// At the end of the path, use right as default
					icon = playerIcons.right || null;
				}
			} else if (playerImage) {
				// Fallback to playerImage if playerIcons not provided
				icon = playerImage;
			}

			return {
				completedPathSet: completedSet,
				playerHexagon: { row: playerRow, col: playerCol },
				currentPlayerIcon: icon,
			};
		}, [
			playerImage,
			playerIcons,
			generatedPath,
			playerPathIndex,
			isActive,
		]);

	// Memoize rows and columns arrays to prevent recreation on every render
	const rowsArray = React.useMemo(
		() => Array.from({ length: gridDimensions.rows }),
		[gridDimensions.rows],
	);
	const columnsArray = React.useMemo(
		() => Array.from({ length: gridDimensions.columns }),
		[gridDimensions.columns],
	);

	return (
		<>
			<div
				ref={containerRef}
				data-slot="hexagon-background"
				className={cn(
					"relative size-full overflow-hidden",
					isTranslucent
						? "dark:bg-transparent bg-black/10"
						: "dark:bg-neutral-900 bg-neutral-100",
					className,
				)}
				{...(props as any)}
			>
				<style>{`:root { --hexagon-margin: ${hexagonMargin}px; }`}</style>
				<div className="absolute top-0 -left-0 size-full overflow-hidden">
					{rowsArray.map((_, rowIndex) => (
						<div
							key={`row-${rowIndex}`}
							style={{
								marginTop: computedMarginTop,
								marginLeft:
									((rowIndex + 1) % 2 === 0
										? evenRowMarginLeft
										: oddRowMarginLeft) - 10,
							}}
							className="inline-flex"
						>
							{columnsArray.map((_, colIndex) => {
								const isHovered =
									resolvedHoveredHexagon?.row === rowIndex &&
									resolvedHoveredHexagon?.col === colIndex;
								const isSelected =
									selectedHexagon?.row === rowIndex &&
									selectedHexagon?.col === colIndex;
								const isPath = pathSet.has(
									`${rowIndex},${colIndex}`,
								);
								const pathIndex = pathIndexMap.get(
									`${rowIndex},${colIndex}`,
								);
								const isCompleted = completedPathSet.has(
									`${rowIndex},${colIndex}`,
								);
								const isPlayerHexagon =
									playerHexagon?.row === rowIndex &&
									playerHexagon?.col === colIndex;
								// Use pre-calculated neighbor set for much faster lookup
								const _isInTrail = hoveredNeighbors.has(
									`${rowIndex},${colIndex}`,
								);

								return (
									<Hexagon
										key={`hexagon-${rowIndex}-${colIndex}`}
										rowIndex={rowIndex}
										colIndex={colIndex}
										hexagonWidth={hexagonWidth}
										hexagonHeight={hexagonHeight}
										hexagonMargin={hexagonMargin}
										isHovered={isHovered}
										isSelected={isSelected}
										isInTrail={hoveredNeighbors.has(
											`${rowIndex},${colIndex}`,
										)}
										isPath={isPath}
										pathIndex={pathIndex}
										hexagonProps={hexagonProps}
										isTranslucent={isTranslucent}
										isCompleted={isCompleted}
										isPlayerHexagon={isPlayerHexagon}
									/>
								);
							})}
						</div>
					))}
				</div>
				{/* Single flag instance positioned based on selectedHexagon */}
				{/* Use key to force remount when selection changes, triggering animation */}
				{isClickable && selectedHexagon && (
					<FlagMarker
						key={`flag-${selectedHexagon.row}-${selectedHexagon.col}`}
						selectedHexagon={selectedHexagon}
						hoveredHexagon={resolvedHoveredHexagon}
						hexagonWidth={hexagonWidth}
						hexagonHeight={hexagonHeight}
						hexagonMargin={hexagonMargin}
						computedMarginTop={computedMarginTop}
						oddRowMarginLeft={oddRowMarginLeft}
						evenRowMarginLeft={evenRowMarginLeft}
						rowSpacing={rowSpacing}
					/>
				)}

				{/* Player image - positioned based on playerPathIndex */}
				{isMounted &&
					currentPlayerIcon &&
					generatedPath &&
					generatedPath.length > 0 &&
					playerHexagon && (
						<HexagonImageOverlay
							key={`player-${path?.refreshKey ?? 0}-${playerPathIndex}-${isActive ? "active" : "inactive"}`}
							row={playerHexagon.row}
							col={playerHexagon.col}
							image={currentPlayerIcon}
							hexagonWidth={hexagonWidth}
							hexagonHeight={hexagonHeight}
							hexagonMargin={hexagonMargin}
							computedMarginTop={computedMarginTop}
							oddRowMarginLeft={oddRowMarginLeft}
							evenRowMarginLeft={evenRowMarginLeft}
							rowSpacing={rowSpacing}
							size="w-12 h-12"
							isPlayer={true}
						/>
					)}

				{/* Path images */}
				{isMounted &&
					pathImagePositions.map((pos) => (
						<HexagonImageOverlay
							key={`path-image-${path?.refreshKey ?? 0}-${pos.row}-${pos.col}`}
							row={pos.row}
							col={pos.col}
							image={pos.image}
							hexagonWidth={hexagonWidth}
							hexagonHeight={hexagonHeight}
							hexagonMargin={hexagonMargin}
							computedMarginTop={computedMarginTop}
							oddRowMarginLeft={oddRowMarginLeft}
							evenRowMarginLeft={evenRowMarginLeft}
							rowSpacing={rowSpacing}
							size={PATH_STOP_OVERLAY_ICON_SIZE_CLASS}
							hoveredHexagon={resolvedHoveredHexagon}
							enableHoverScale={true}
							stop={pos.stop}
							gridDimensions={gridDimensions}
							onTransientGridHover={setTransientHoveredCell}
						/>
					))}

				{/* Environment images */}
				{isMounted &&
					environmentImagePositions.map((pos) => {
						return (
							<HexagonImageOverlay
								key={`env-image-${featureIndex ?? 0}-${path?.refreshKey ?? 0}-${pos.row}-${pos.col}`}
								row={pos.row}
								col={pos.col}
								image={pos.image}
								hexagonWidth={hexagonWidth}
								hexagonHeight={hexagonHeight}
								hexagonMargin={hexagonMargin}
								computedMarginTop={computedMarginTop}
								oddRowMarginLeft={oddRowMarginLeft}
								evenRowMarginLeft={evenRowMarginLeft}
								rowSpacing={rowSpacing}
								pulseDelay={pos.pulseDelay}
								isInteractable={false}
								enableHoverScale={false}
								applyLeftOffset={true}
								hoveredHexagon={resolvedHoveredHexagon}
								gridDimensions={gridDimensions}
								featureIndex={featureIndex}
								flipHorizontal={pos.flipHorizontal}
							/>
						);
					})}

				{/* Render topics separately */}
				{topicPositions.map((pos, index) => {
					const info = topicInfo[index];
					return (
						<HexagonImageOverlay
							key={`interactable-${path?.refreshKey ?? 0}-${pos.row}-${pos.col}-${index}`}
							row={pos.row}
							col={pos.col}
							image={
								"data:image/svg+xml;charset=utf-8," +
								encodeURIComponent(
									'<svg width="32" height="32" xmlns="http://www.w3.org/2000/svg"><circle cx="16" cy="16" r="12" fill="rgb(15,23,42)" fill-opacity="0.7"/><circle cx="16" cy="16" r="8" fill="rgb(15,23,42)" fill-opacity="0.9"/></svg>',
								)
							}
							hexagonWidth={hexagonWidth}
							hexagonHeight={hexagonHeight}
							hexagonMargin={hexagonMargin}
							computedMarginTop={computedMarginTop}
							oddRowMarginLeft={oddRowMarginLeft}
							evenRowMarginLeft={evenRowMarginLeft}
							rowSpacing={rowSpacing}
							pulseDelay={pos.pulseDelay}
							isInteractable={true}
							hoveredHexagon={resolvedHoveredHexagon}
							onHover={handleTopicHover}
							onTransientGridHover={setTransientHoveredCell}
							topicIndex={index}
							topicInfo={info}
							hoverDuration={pos.hoverDuration}
							gridDimensions={gridDimensions}
							featureIndex={featureIndex}
						/>
					);
				})}

				{children}
			</div>
		</>
	);
}

// Memoize HexagonBackground to prevent unnecessary re-renders
const MemoizedHexagonBackground = React.memo(
	HexagonBackground,
	(prevProps, nextProps) => {
		// Compare topics by checking if hasBeenRead values have changed
		const topicsEqual = (() => {
			if (!prevProps.topics && !nextProps.topics) {
				return true;
			}
			if (!prevProps.topics || !nextProps.topics) {
				return false;
			}
			if (
				prevProps.topics.topicInfo?.length !==
				nextProps.topics.topicInfo?.length
			) {
				return false;
			}

			// Check if any hasBeenRead values have changed
			for (
				let i = 0;
				i < (prevProps.topics.topicInfo?.length ?? 0);
				i++
			) {
				const prevRead =
					prevProps.topics.topicInfo?.[i]?.hasBeenRead ?? false;
				const nextRead =
					nextProps.topics.topicInfo?.[i]?.hasBeenRead ?? false;
				if (prevRead !== nextRead) {
					return false;
				}
			}

			// Also check stable config values (these are now in topicConfig, but we check the top-level for backwards compatibility)
			return (
				prevProps.topics.topicInfo?.length ===
					nextProps.topics.topicInfo?.length &&
				prevProps.topics.topicConfig?.minRow ===
					nextProps.topics.topicConfig?.minRow &&
				prevProps.topics.topicConfig?.maxRow ===
					nextProps.topics.topicConfig?.maxRow &&
				prevProps.topics.topicConfig?.minCol ===
					nextProps.topics.topicConfig?.minCol &&
				prevProps.topics.topicConfig?.maxCol ===
					nextProps.topics.topicConfig?.maxCol &&
				prevProps.topics.topicConfig?.minDistanceToPath ===
					nextProps.topics.topicConfig?.minDistanceToPath &&
				prevProps.topics.topicConfig?.maxDistanceToPath ===
					nextProps.topics.topicConfig?.maxDistanceToPath &&
				prevProps.topics.topicConfig?.minSpacing ===
					nextProps.topics.topicConfig?.minSpacing &&
				prevProps.topics.topicConfig?.hoverDuration ===
					nextProps.topics.topicConfig?.hoverDuration &&
				prevProps.topics.topicConfig?.autoPlay ===
					nextProps.topics.topicConfig?.autoPlay
			);
		})();

		return (
			prevProps.hexagonSize === nextProps.hexagonSize &&
			prevProps.hexagonMargin === nextProps.hexagonMargin &&
			prevProps.hoveredHexagon?.row === nextProps.hoveredHexagon?.row &&
			prevProps.hoveredHexagon?.col === nextProps.hoveredHexagon?.col &&
			prevProps.selectedHexagon?.row === nextProps.selectedHexagon?.row &&
			prevProps.selectedHexagon?.col === nextProps.selectedHexagon?.col &&
			prevProps.isTranslucent === nextProps.isTranslucent &&
			prevProps.isClickable === nextProps.isClickable &&
			prevProps.isActive === nextProps.isActive &&
			JSON.stringify(prevProps.path) === JSON.stringify(nextProps.path) &&
			JSON.stringify(prevProps.pathImages) ===
				JSON.stringify(nextProps.pathImages) &&
			prevProps.playerImage === nextProps.playerImage &&
			JSON.stringify(prevProps.playerIcons) ===
				JSON.stringify(nextProps.playerIcons) &&
			JSON.stringify(prevProps.environmentImages) ===
				JSON.stringify(nextProps.environmentImages) &&
			topicsEqual
		);
	},
);

MemoizedHexagonBackground.displayName = "HexagonBackground";

export {
	MemoizedHexagonBackground as HexagonBackground,
	type HexagonBackgroundProps,
	LANDING_PAGE_DEMO_PATH_STOPS,
	type LandingPathStopIcon,
};
